import type { Model, ModelFilters, ModelListResponse, CategoryListResponse } from '@/types/models'
import { API_BASE, createFetchOptions } from './config'

export const modelsApi = {
  // Get list of models
  async getModels(filters?: ModelFilters, token?: string): Promise<ModelListResponse> {
    const params = new URLSearchParams()
    
    if (filters) {
      if (filters.category) params.append('category', filters.category)
      if (filters.search) params.append('search', filters.search)
      if (filters.visibility) params.append('visibility', filters.visibility)
      if (filters.sort) params.append('sort', filters.sort)
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.offset) params.append('offset', filters.offset.toString())
      if (filters.tags) {
        filters.tags.forEach(tag => params.append('tags', tag))
      }
    }

    const headers: HeadersInit = {
      'X-User-Email': localStorage.getItem('userEmail') || '',
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
      headers['X-Admin-Email'] = localStorage.getItem('adminEmail') || ''
    }

    const response = await fetch(`${API_BASE}/api/v2/models?${params}`, 
      createFetchOptions({ headers })
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error('API Error:', response.status, errorText)
      throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`)
    }

    return response.json()
  },

  // Get single model
  async getModel(id: string): Promise<{ success: boolean; data: Model }> {
    const response = await fetch(`${API_BASE}/api/v2/models/${id}`, 
      createFetchOptions({
        headers: {
          'X-User-Email': localStorage.getItem('userEmail') || '',
        },
      })
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch model: ${response.statusText}`)
    }

    return response.json()
  },

  // Get categories
  async getCategories(): Promise<CategoryListResponse> {
    const response = await fetch(`${API_BASE}/api/v2/models/categories`, createFetchOptions())

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`)
    }

    return response.json()
  },

  // Record model view
  async recordView(modelId: string, duration?: number): Promise<void> {
    await fetch(`${API_BASE}/api/v2/models/${modelId}/view`, 
      createFetchOptions({
        method: 'POST',
        body: JSON.stringify({ duration }),
      })
    )
  },

  // Get model download URL
  getDownloadUrl(modelId: string): string {
    return `${API_BASE}/api/v2/models/${modelId}/download`
  },

  // Get thumbnail URL
  getThumbnailUrl(modelId: string, size: '256' | '512' | '1024' = '512'): string {
    return `${API_BASE}/api/v2/models/${modelId}/thumbnail?size=${size}`
  },

  // Search models
  async searchModels(query: string): Promise<{ success: boolean; data: Model[] }> {
    const response = await fetch(`${API_BASE}/api/v2/models/search?q=${encodeURIComponent(query)}`, createFetchOptions())

    if (!response.ok) {
      throw new Error(`Search failed: ${response.statusText}`)
    }

    return response.json()
  },
}